import { useMidi } from '@tunasong/audio-ui'
import { addToolBreadcrumb } from '@tunasong/models'
import {
  MelodyLib,
  VocalLib,
  computeMelodyTrainerStats,
  toPhraseFeedback,
  toTimingFeedback,
  type CaptureDiagnosis,
  type MelodyTrainerAttempt,
  type MelodyTrainerMode,
  type PhraseFeedback,
  type PitchFrame,
  type TimingFeedback,
} from '@tunasong/music-lib'
import type { Melody } from '@tunasong/schemas'
import { useUserConfig } from '@tunasong/ui-lib'
import { useEffect, useRef, useState } from 'react'
import type { FC } from 'react'
import { AUDIO_LEAD_IN_MS } from '../melody-capture/capture-take.js'
import { buildClickTrack, CLICK_INSTRUMENT } from '../melody-capture/click-track.js'
import { hasHeadphoneConsent, withHeadphoneConsent } from '../melody-capture/headphone-consent.js'
import { useAudioCapture } from '../melody-capture/use-audio-capture.js'
import { useMidiCapture } from '../melody-capture/use-midi-capture.js'
import { useMelodyRollPlayback } from '../melody-roll/use-melody-roll-playback.js'
import { useCaptureMonitor } from '../pitch-training/use-capture-monitor.js'
import { usePlayNotes } from '../player/piano-player.hook.js'
import type { Stab } from '../player/stab-scheduler.js'
import { LatencyCalibrationPanel, type LatencyTapSource } from './latency-calibration-panel.js'
import { getInputLatency, withInputLatency } from './latency-store.js'
import { latencyClickTimesMs, latencyClickTrack, latencyRunDurationMs } from './latency-taps.js'
import { MelodyTrainerFeedback } from './melody-trainer-feedback.js'
import { MelodyTrainerQuestion } from './melody-trainer-question.js'
import { MelodyTrainerSummary } from './melody-trainer-summary.js'
import { MelodyTrainerWelcome } from './melody-trainer-welcome.js'
import { runTrainerAttempt, type AttemptPhase } from './trainer-attempt.js'
import { attemptTimeline } from './trainer-timeline.js'
import styles from './melody-trainer.module.css'

/**
 * Melody Trainer v1 - sing or play a melody back, scored on pitch AND timing.
 *
 * Every attempt is started by a click, never by an effect. Two reasons, and the second is the
 * load-bearing one: a browser will not resume an `AudioContext` without a user gesture, and an
 * effect re-firing on an unstable dependency would re-open the microphone mid-take. The same rule
 * the Pitch Trainer and Melody Capture both state, for the same reason.
 *
 * All three modes score through ONE path: `alignNotes` decides which note is which, `scorePhrase`
 * judges the pitches, `scoreTiming` measures the onsets on the pairs the aligner chose. Three
 * paths would be three sets of rules about what a wrong note is, and they would drift. What
 * differs per mode is only what a performance is made of before it reaches that path - segmented
 * frames, or MIDI notes.
 *
 * `alignNotes` is called here rather than plumbed out of `scorePhrase`: it is pure and
 * deterministic, so the two calls agree by construction, and `VocalLib` is not this build's to
 * change.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §6
 */

/**
 * §6: sessions of N attempts on the same melody, default 5.
 *
 * Module-private, as the Pitch Trainer's `ITEMS_PER_SESSION` is: a session length is this file's
 * default, not an API, and exporting it from a component module breaks fast refresh.
 */
const ATTEMPTS_PER_SESSION = 5

const wait = (ms: number) => new Promise<void>(resolve => window.setTimeout(resolve, ms))

type TrainerState =
  | { phase: 'welcome'; preSelected: MelodyTrainerMode | null }
  | {
      phase: 'calibrating-latency'
      mode: MelodyTrainerMode
      tapSource: LatencyTapSource
      running: boolean
      tapCount: number
      attempted: boolean
      result: MelodyLib.LatencyCalibration | null
    }
  | {
      phase: 'asking'
      mode: MelodyTrainerMode
      index: number
      attempts: MelodyTrainerAttempt[]
      capture: AttemptPhase | null
    }
  | {
      phase: 'feedback'
      mode: MelodyTrainerMode
      index: number
      attempts: MelodyTrainerAttempt[]
      pitch: PhraseFeedback
      timing: TimingFeedback | null
      diagnosis: CaptureDiagnosis | null
      /** Folded here, where the signed mean still exists - `TimingFeedback` keeps only the size. */
      attempt: MelodyTrainerAttempt
    }
  | { phase: 'done'; mode: MelodyTrainerMode; attempts: MelodyTrainerAttempt[] }

const isAudioMode = (mode: MelodyTrainerMode) => mode !== 'midi'

/** Voice mode hears what it is asked to sing: the reference sounds at the transposed pitch too. */
const transposeMelody = (melody: Melody, semitones: number): Melody =>
  semitones === 0 ? melody : { ...melody, notes: melody.notes.map(note => ({ ...note, midi: note.midi + semitones })) }

/**
 * One attempt's contribution to the session summary.
 *
 * Folded from the SCORE rather than from the feedback, because `TimingFeedback` carries only the
 * size of the mean - the direction lives in its lexicon id - and the summary needs to average
 * signed numbers. Averaging unsigned ones would report a session that rushed as much as it dragged
 * as one that did neither, which is a different claim.
 */
const toAttempt = (pitch: PhraseFeedback, timing: MelodyLib.TimingScore | null): MelodyTrainerAttempt => ({
  meanScore: pitch.scorePercent === null ? null : pitch.scorePercent / 100,
  meanErrorMs: timing?.meanErrorMs ?? null,
  tendency: timing?.tendency ?? null,
})

export interface MelodyTrainerProps {
  melody: Melody
  /** Attempts in a session. Defaults to §6's five. */
  attempts?: number
  /** Present when the trainer is a dialog over a melody rather than its own page. */
  onClose?: () => void
}

export const MelodyTrainer: FC<MelodyTrainerProps> = props => {
  const { melody, attempts: attemptsPerSession = ATTEMPTS_PER_SESSION, onClose } = props

  const [userConfig, updateConfig] = useUserConfig()
  const audio = useAudioCapture()
  const midiCapture = useMidiCapture()
  const monitor = useCaptureMonitor()
  const { playNotes: playClickNotes } = usePlayNotes(CLICK_INSTRUMENT)

  const [state, setState] = useState<TrainerState>({ phase: 'welcome', preSelected: null })

  /** Every scheduled click, so Cancel and unmount can drop the ones that have not sounded. */
  const timeouts = useRef<number[]>([])
  /** Taps collected during a latency run, on `performance.now()`. */
  const taps = useRef<number[]>([])
  /** When the calibration clicks started, on the same clock. */
  const clicksStartedAt = useRef(0)
  /** ...and on the frames' re-based clock, for the audio tap source. */
  const clicksStartedFrameMs = useRef(0)
  const collectingTaps = useRef(false)

  /**
   * A second `useMidi` beside `useMidiCapture`'s. It listens only while a latency run is
   * collecting, and `useMidiCapture` only records while a take has armed it, so the two never
   * both claim an event.
   */
  const { inputs: midiInputs } = useMidi({
    onNoteEvent: event => {
      if (!collectingTaps.current || event.type !== 'noteon') {
        return
      }
      taps.current = [...taps.current, performance.now()]
      setState(current =>
        current.phase === 'calibrating-latency' ? { ...current, tapCount: taps.current.length } : current
      )
    },
  })

  const clearTimeouts = () => {
    for (const handle of timeouts.current) {
      window.clearTimeout(handle)
    }
    timeouts.current = []
  }

  useEffect(() => {
    addToolBreadcrumb('opened', { tool: 'melody-trainer' })
    return clearTimeouts
  }, [])

  const range = userConfig?.musicPreferences?.vocalRange ?? null
  const confirmedDevices = userConfig?.musicPreferences?.headphoneConfirmedDevices

  const referenceMidis = melody.notes.map(note => note.midi)
  const voiceMidis = range ? VocalLib.transposeIntoRange(referenceMidis, range) : referenceMidis
  const transposedBy = referenceMidis.length === 0 ? 0 : voiceMidis[0] - referenceMidis[0]

  const activeMode = state.phase === 'welcome' ? null : state.mode
  const practiceMelody = activeMode === 'voice' ? transposeMelody(melody, transposedBy) : melody
  const playback = useMelodyRollPlayback({ melody: practiceMelody })

  /*
   * The device this session's numbers belong to. Null before the microphone has ever been opened,
   * which is exactly the state "uncalibrated" describes - and running the calibration is what
   * names it.
   */
  const takeDeviceLabel = activeMode === 'midi' ? (midiInputs[0]?.name ?? null) : audio.deviceLabel
  const inputLatencyMs = getInputLatency(userConfig?.musicPreferences?.inputLatencyByDevice, takeDeviceLabel)
  const welcomeLatencyMs = getInputLatency(
    userConfig?.musicPreferences?.inputLatencyByDevice,
    audio.deviceLabel ?? (midiInputs[0]?.name ?? null)
  )

  const scheduleClicks = (stabs: Stab[]) => {
    for (const stab of stabs) {
      timeouts.current.push(window.setTimeout(() => playClickNotes(stab.events), stab.delayMs))
    }
  }

  // ───────────────────────── latency calibration ─────────────────────────

  const runLatencyTest = async (mode: MelodyTrainerMode, tapSource: LatencyTapSource) => {
    taps.current = []
    monitor.reset()
    setState({ phase: 'calibrating-latency', mode, tapSource, running: true, tapCount: 0, attempted: true, result: null })

    /*
     * The microphone is opened for every audio-mode run, whichever source the taps come from: it is
     * what NAMES the device the measurement will be filed under, and without a name nothing is
     * stored and the gate never lifts.
     */
    let origin: number | null = null
    let latestMs = 0
    const frames: PitchFrame[] = []
    const stopFrames = isAudioMode(mode)
      ? await audio.startFrames(frame => {
          origin = origin ?? frame.time
          const rebased = { ...frame, time: frame.time - origin }
          latestMs = rebased.time * 1000
          frames.push(rebased)
          monitor.onFrame(rebased)
        })
      : undefined

    collectingTaps.current = true
    try {
      /* Room tone first, so the voicing gate has a floor that is not the click. */
      await wait(isAudioMode(mode) ? AUDIO_LEAD_IN_MS : 0)
      clicksStartedAt.current = performance.now()
      clicksStartedFrameMs.current = latestMs
      scheduleClicks(latencyClickTrack())
      await wait(latencyRunDurationMs())
    } finally {
      collectingTaps.current = false
      clearTimeouts()
      stopFrames?.()
    }

    /*
     * An audio run's taps are the onsets of what was sung or played, found with the Pitch Trainer's
     * segmenter rather than a second onset detector written here - the rules about what counts as
     * the start of a note already exist and are Bowie's.
     */
    const tapTimesMs =
      tapSource === 'audio'
        ? MelodyLib.segmentOnsetsMs(VocalLib.segmentNotes(VocalLib.voicedFrames(frames)), clicksStartedFrameMs.current)
        : taps.current.map(tap => tap - clicksStartedAt.current)

    const result = MelodyLib.latencyFromTaps(latencyClickTimesMs(), tapTimesMs)

    setState({
      phase: 'calibrating-latency',
      mode,
      tapSource,
      running: false,
      tapCount: tapTimesMs.length,
      attempted: true,
      result,
    })

    if (result === null) {
      return
    }
    const deviceLabel = isAudioMode(mode) ? audio.deviceLabel : (midiInputs[0]?.name ?? null)
    updateConfig('musicPreferences', {
      ...userConfig?.musicPreferences,
      inputLatencyByDevice: withInputLatency(
        userConfig?.musicPreferences?.inputLatencyByDevice,
        deviceLabel,
        result.inputLatencyMs
      ),
    })
    addToolBreadcrumb('calibrated', { tool: 'melody-trainer' })
  }

  // ───────────────────────── one attempt ─────────────────────────

  const runAttempt = async (mode: MelodyTrainerMode, index: number, previous: MelodyTrainerAttempt[]) => {
    const timeline = attemptTimeline(practiceMelody)
    const clickTrack = buildClickTrack({
      bpm: practiceMelody.tempo.bpm,
      meter: practiceMelody.tempo.meter,
      bars: timeline.bars,
      /* The count-in already sounded, before the reference. This click starts ON beat 1. */
      countIn: false,
    })

    monitor.reset()
    if (mode === 'midi') {
      midiCapture.arm()
    }
    setState({ phase: 'asking', mode, index, attempts: previous, capture: null })

    const result = await runTrainerAttempt(
      {
        startFrames: isAudioMode(mode) ? audio.startFrames : undefined,
        /* `bars: 0` leaves exactly the two count-in knocks, at the melody's own tempo. */
        playCountIn: () =>
          scheduleClicks(
            buildClickTrack({ bpm: practiceMelody.tempo.bpm, meter: practiceMelody.tempo.meter, bars: 0 })
          ),
        playReference: playback.play,
        stopReference: playback.stop,
        playClick: () => scheduleClicks(clickTrack),
        wait,
        now: () => performance.now(),
        onPhase: capture =>
          setState(current =>
            current.phase === 'asking' && current.index === index ? { ...current, capture } : current
          ),
        onFrame: isAudioMode(mode) ? monitor.onFrame : undefined,
      },
      { ...timeline, leadInMs: isAudioMode(mode) ? AUDIO_LEAD_IN_MS : 0 }
    )
    clearTimeouts()

    const practiceMidis = practiceMelody.notes.map(note => note.midi)
    const raw =
      mode === 'midi'
        ? midiCapture.collect(result.t0WallMs, result.t0WallMs + timeline.takeMs + timeline.tailMs)
        : []
    const sung =
      mode === 'midi'
        ? MelodyLib.rawNotesToSungNotes(raw)
        : VocalLib.segmentNotes(VocalLib.voicedFrames(result.frames))
    const performedOnsetsMs =
      mode === 'midi' ? raw.map(note => note.onsetMs) : MelodyLib.segmentOnsetsMs(sung, result.t0Ms)

    /*
     * MIDI is scored STRICTLY: a keyboard has every octave, so an octave-out performance is wrong
     * notes rather than the range accommodation §5.7's transpose rule exists to give a voice or a
     * mic'd instrument whose range the melody may exceed (Bowie, 2026-09-09).
     */
    const pitchScore = VocalLib.scorePhrase(sung, practiceMidis, {
      octaveRule: mode === 'midi' ? 'strict' : 'transpose',
    })
    const events = MelodyLib.scheduleMelody(practiceMelody)
    const timingScore = MelodyLib.scoreTiming({
      pairs: VocalLib.alignNotes(sung, practiceMidis).pairs,
      performedOnsetsMs,
      referenceOnsetsMs: events.map(event => event.time),
      referenceOffsetsMs: practiceMelody.notes.map(note => note.offsetMs),
      inputLatencyMs: inputLatencyMs ?? 0,
    })

    const pitch = toPhraseFeedback(pitchScore)
    /* Only when nothing was heard, and only where there was a microphone to diagnose. */
    const diagnosis = pitchScore === null && isAudioMode(mode) ? VocalLib.diagnoseCapture(result.frames) : null

    addToolBreadcrumb('performed', { tool: 'melody-trainer', mode, heard: !pitch.nothingHeard })

    setState({
      phase: 'feedback',
      mode,
      index,
      attempts: previous,
      pitch,
      timing: pitch.nothingHeard ? null : toTimingFeedback(timingScore),
      diagnosis,
      attempt: toAttempt(pitch, pitch.nothingHeard ? null : timingScore),
    })
  }

  // ───────────────────────── render ─────────────────────────

  if (state.phase === 'welcome') {
    return (
      <div className={styles.container}>
        <MelodyTrainerWelcome
          melody={melody}
          preSelected={state.preSelected}
          range={range}
          inputLatencyMs={welcomeLatencyMs}
          deviceLabel={audio.deviceLabel}
          headphonesConfirmed={hasHeadphoneConsent(confirmedDevices, audio.deviceLabel)}
          midiAvailable={midiInputs.length > 0}
          transposedBy={transposedBy}
          onConfirmHeadphones={confirmed => {
            if (!confirmed) {
              return
            }
            updateConfig('musicPreferences', {
              ...userConfig?.musicPreferences,
              headphoneConfirmedDevices: withHeadphoneConsent(confirmedDevices, audio.deviceLabel),
            })
          }}
          onCalibrateLatency={mode =>
            void runLatencyTest(mode, mode === 'midi' ? 'midi' : 'audio')
          }
          onStart={mode => void runAttempt(mode, 0, [])}
        />
      </div>
    )
  }

  if (state.phase === 'calibrating-latency') {
    return (
      <div className={styles.container}>
        <LatencyCalibrationPanel
          tapSource={state.tapSource}
          deviceLabel={takeDeviceLabel}
          running={state.running}
          tapCount={state.tapCount}
          result={state.result}
          attempted={state.attempted}
          onStart={() => void runLatencyTest(state.mode, state.tapSource)}
          onTap={() => {
            taps.current = [...taps.current, performance.now()]
            setState(current =>
              current.phase === 'calibrating-latency' ? { ...current, tapCount: taps.current.length } : current
            )
          }}
          onUseKeyboard={() =>
            setState(current =>
              current.phase === 'calibrating-latency' ? { ...current, tapSource: 'keyboard' } : current
            )
          }
          onDone={() => setState({ phase: 'welcome', preSelected: state.mode })}
          onCancel={() => {
            clearTimeouts()
            setState({ phase: 'welcome', preSelected: state.mode })
          }}
          meter={
            isAudioMode(state.mode)
              ? { frame: monitor.frame, noiseFloorRms: monitor.noiseFloorRms, deviceLabel: audio.deviceLabel }
              : undefined
          }
        />
      </div>
    )
  }

  if (state.phase === 'asking') {
    return (
      <div className={styles.container}>
        <MelodyTrainerQuestion
          melody={practiceMelody}
          index={state.index}
          total={attemptsPerSession}
          phase={state.capture}
          playheadMs={playback.playheadMs}
          meter={
            isAudioMode(state.mode)
              ? { frame: monitor.frame, noiseFloorRms: monitor.noiseFloorRms, deviceLabel: audio.deviceLabel }
              : undefined
          }
        />
      </div>
    )
  }

  if (state.phase === 'feedback') {
    const attemptsSoFar = [...state.attempts, state.attempt]
    return (
      <div className={styles.container}>
        <MelodyTrainerQuestion
          melody={practiceMelody}
          index={state.index}
          total={attemptsPerSession}
          phase={null}
          playheadMs={playback.playheadMs}
        />
        <MelodyTrainerFeedback
          pitch={state.pitch}
          diagnosis={state.diagnosis}
          timing={state.timing}
          onRetry={() => void runAttempt(state.mode, state.index, state.attempts)}
          onNext={() => {
            if (state.index + 1 >= attemptsPerSession) {
              setState({ phase: 'done', mode: state.mode, attempts: attemptsSoFar })
              return
            }
            void runAttempt(state.mode, state.index + 1, attemptsSoFar)
          }}
        />
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <MelodyTrainerSummary
        stats={computeMelodyTrainerStats(state.attempts)}
        onRestart={() => void runAttempt(state.mode, 0, [])}
        onClose={onClose}
      />
    </div>
  )
}
